/**
 * Requisicoes de Separacao (Suprimentos) — controle do almoxarife para as
 * requisicoes vindas do Revisor de Ferragens. 4 vistas (tabela / kanban lateral /
 * linha do tempo / calendario), filtros, KPIs, acoes em lote, configuracao das
 * colunas do kanban e impressao do documento de revisao (reusa o do revisor).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useRespostaMaisRecente, useValorComAtraso } from "../../../hooks/useBuscaRemota";
import {
  FiCalendar, FiClock, FiColumns, FiList, FiRefreshCw, FiSliders,
} from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { PageContainer } from "../../../styles/shared";
import { ambientesSalvosParaDocumento } from "../../RevisorFerragens/lib/documento";
import { FolhasImpressao } from "../../RevisorFerragens/Impressao";
import {
  SITUACAO_LABEL,
  SITUACAO_TONE,
  SITUACOES_SEPARACAO,
  cancelarRequisicao,
  iniciarRequisicao,
  listarRequisicoes,
  loteRequisicoes,
  moverEtapaRequisicao,
  obterColunasKanbanSeparacao,
  obterRequisicao,
  reabrirRequisicao,
  voltarParaPendentes,
  salvarColunasKanbanSeparacao,
  separarRequisicao,
} from "../../../services/separacaoFerragens";
import Calendario from "../_shared/quadro/Calendario";
import ColunasPanel from "../_shared/quadro/ColunasPanel";
import Kanban from "../_shared/quadro/Kanban";
import LinhaTempo from "../_shared/quadro/LinhaTempo";
import Tabela from "../_shared/quadro/Tabela";
import {
  escreverComprovanteSeparacaoFerragens,
  imprimirComprovanteSeparacaoFerragens,
} from "./comprovanteSeparacao";
import Detalhe from "./Detalhe";
import { transicaoParaColuna } from "./transicoes";
import {
  BatchBar, EmptyBox, FilterBar, SearchInput, Select, ToggleBtn, ViewToggle,
} from "./styles";
import { Carregando } from "../_shared/Estados";

/** Vocabulário que os componentes compartilhados de quadro recebem por prop. */
const VOCAB = { SITUACAO_LABEL, SITUACAO_TONE };

const VISTAS = [
  { id: "tabela", label: "Tabela", icone: FiList },
  { id: "kanban", label: "Kanban", icone: FiColumns },
  { id: "linha", label: "Linha do tempo", icone: FiClock },
  { id: "calendario", label: "Calendário", icone: FiCalendar },
];

export default function RequisicoesSeparacao() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();

  const podeGerir = isMaster || hasAllPermissions(["separacao_ferragens:gerir"]);
  const podeConfigurar = isMaster || hasAllPermissions(["separacao_ferragens:configurar"]);

  const [todas, setTodas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscaInput, setBuscaInput] = useState("");
  const [filtroSituacao, setFiltroSituacao] = useState("");
  const [vista, setVista] = useState("tabela");
  const [selecionadas, setSelecionadas] = useState(new Set());
  const [colunas, setColunas] = useState([]);
  const [colunasOpen, setColunasOpen] = useState(false);
  const [detalheId, setDetalheId] = useState(null);
  const [detalheReq, setDetalheReq] = useState(null);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [imprimirReq, setImprimirReq] = useState(null);

  const busca = useValorComAtraso(buscaInput.trim());
  const iniciarCarga = useRespostaMaisRecente();

  const carregar = useCallback(async () => {
    const vale = iniciarCarga();
    setLoading(true);
    try {
      // Busca todas (sem filtro de situacao) — o kanban precisa de todos os
      // status e os KPIs contam o conjunto inteiro. Situacao filtra no cliente.
      const res = await listarRequisicoes(request, { busca });
      // Resposta de uma busca já superada não pode vencer a mais recente.
      if (!vale()) return;
      setTodas(Array.isArray(res) ? res : res.results || []);
    } catch {
      if (vale()) setTodas([]);
    } finally {
      if (vale()) setLoading(false);
    }
  }, [request, busca, iniciarCarga]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    obterColunasKanbanSeparacao(request)
      .then((res) => setColunas(res.colunas || []))
      .catch(() => setColunas([]));
  }, [request]);

  const filtradas = useMemo(
    () => (filtroSituacao ? todas.filter((r) => r.situacao === filtroSituacao) : todas),
    [todas, filtroSituacao],
  );

  const kpis = useMemo(() => {
    const c = { PENDENTE: 0, EM_SEPARACAO: 0, SEPARADA: 0, CANCELADA: 0 };
    todas.forEach((r) => { c[r.situacao] = (c[r.situacao] || 0) + 1; });
    return [
      { key: "pend", label: "Pendentes", value: String(c.PENDENTE), tone: c.PENDENTE ? "warning" : undefined },
      { key: "sep", label: "Em separação", value: String(c.EM_SEPARACAO), tone: c.EM_SEPARACAO ? "info" : undefined },
      { key: "ok", label: "Separadas", value: String(c.SEPARADA), tone: c.SEPARADA ? "success" : undefined },
      { key: "tot", label: "Total", value: String(todas.length) },
    ];
  }, [todas]);

  // ---- detalhe ----
  const carregarDetalhe = useCallback(async (id) => {
    setDetalheLoading(true);
    try {
      setDetalheReq(await obterRequisicao(request, id));
    } catch {
      // Falha transitória num RELOAD não descarta o que já está na tela
      // (zerar aqui prendia o painel num "Carregando…" eterno). Se nunca
      // carregou, fica null e o Detalhe mostra o estado de erro.
      setDetalheReq((prev) => prev || null);
    } finally {
      setDetalheLoading(false);
    }
  }, [request]);

  function abrirDetalhe(id) {
    setDetalheId(id);
    setDetalheReq(null);
    carregarDetalhe(id);
  }

  function fecharDetalhe() {
    setDetalheId(null);
    setDetalheReq(null);
  }

  // ---- acoes ----
  async function aplicarAcao(req, acao, motivo) {
    const fns = {
      iniciar: () => iniciarRequisicao(request, req.id),
      separar: () => separarRequisicao(request, req.id),
      cancelar: () => cancelarRequisicao(request, req.id, motivo),
      reabrir: () => reabrirRequisicao(request, req.id),
      voltar_pendentes: () => voltarParaPendentes(request, req.id),
    };
    try {
      await fns[acao]();
      toast.success("Requisição atualizada.");
      await carregar();
      if (detalheId === req.id) await carregarDetalhe(req.id);
      return true;
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha na operação.");
      return false;
    }
  }

  function acaoDoDetalhe(acao, motivo) {
    if (detalheReq) aplicarAcao(detalheReq, acao, motivo);
  }

  // Drop no kanban: decide a transicao (situacao) e move a coluna operacional,
  // na ordem certa. Cancelar pede motivo; abortar o prompt nao move nada.
  async function soltarNaColuna(req, coluna) {
    const acao = transicaoParaColuna(req.situacao, coluna.situacoes);
    if (acao === "invalid") return;
    let motivo = "";
    if (acao === "cancelar") {
      const m = window.prompt("Motivo do cancelamento:");
      if (!m || !m.trim()) return; // aborta: nao transiciona nem move
      motivo = m.trim();
    }
    if (acao) {
      const ok = await aplicarAcao(req, acao, motivo);
      if (!ok) return; // transicao falhou — nao move a coluna
    }
    await moverEtapa(req, coluna.id);
  }

  // Concluir a separação a partir do checklist: transiciona pra SEPARADA e
  // escreve o comprovante na janela PRÉ-ABERTA no clique (o Detalhe abre a
  // janela sincronicamente — depois dos awaits o navegador bloquearia o
  // pop-up). Cadeia curta: 1 POST + 1 GET antes do comprovante; a lista
  // recarrega em 2º plano.
  async function separarEImprimir(req, janela) {
    try {
      await separarRequisicao(request, req.id);
    } catch (err) {
      if (janela) janela.close();
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao concluir a separação.");
      return;
    }
    toast.success("Requisição separada.");
    let fresh = null;
    try {
      fresh = await obterRequisicao(request, req.id);
    } catch { /* fallback abaixo */ }
    if (fresh) {
      setDetalheReq(fresh);
      if (!escreverComprovanteSeparacaoFerragens(janela, fresh)) {
        toast.error("Não foi possível abrir o comprovante (pop-up bloqueado?). Use Reimprimir.");
      }
    } else {
      if (janela) janela.close();
      toast.error("Separada com sucesso, mas falhou ao carregar o comprovante — use Reimprimir.");
    }
    carregar();
  }

  function reimprimirComprovante(req) {
    if (!imprimirComprovanteSeparacaoFerragens(req)) {
      toast.error("Não foi possível abrir a impressão (pop-up bloqueado?). Libere pop-ups e tente de novo.");
    }
  }

  async function moverEtapa(req, etapaId) {
    setTodas((ts) => ts.map((r) => (r.id === req.id ? { ...r, etapa_quadro: etapaId } : r)));
    try {
      await moverEtapaRequisicao(request, req.id, etapaId);
    } catch {
      await carregar();
    }
  }

  // ---- selecao em lote ----
  function toggleSel(id) {
    setSelecionadas((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  async function acaoLote(acao) {
    const ids = Array.from(selecionadas);
    if (!ids.length) return;
    let motivo = "";
    if (acao === "cancelar") {
      motivo = window.prompt("Motivo do cancelamento (aplicado a todas):") || "";
      if (!motivo.trim()) return;
    }
    try {
      const res = await loteRequisicoes(request, ids, acao, motivo.trim());
      toast.success(`${res.total_ok} requisição(ões) atualizada(s).`);
      setSelecionadas(new Set());
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha na ação em lote.");
    }
  }

  async function salvarColunas(cols) {
    try {
      const res = await salvarColunasKanbanSeparacao(request, cols);
      setColunas(res.colunas || cols);
      setColunasOpen(false);
      toast.success("Colunas salvas.");
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Falha ao salvar colunas.");
    }
  }

  // ---- impressao (reusa o documento do revisor) ----
  if (imprimirReq) {
    const ambientes = imprimirReq.lista ? ambientesSalvosParaDocumento(imprimirReq.lista) : [];
    return (
      <FolhasImpressao
        ambientes={ambientes}
        onVoltar={() => {
          setImprimirReq(null);
          // A impressão DESMONTA a página (e o checklist local); ao voltar,
          // refetcha o detalhe pra reidratar os checks do estado persistido.
          if (detalheId) carregarDetalhe(detalheId);
        }}
      />
    );
  }

  function onImprimir(req) {
    // o detalhe ja tem `lista` embutida; garante que veio completo
    if (req?.lista) setImprimirReq(req);
    else toast.error("Não foi possível carregar o documento desta requisição.");
  }

  const vazio = !loading && filtradas.length === 0;

  return (
    <PageContainer>
      <EntityHeader
        title="Requisições de Separação"
        subtitle="Ferragens enviadas pelo revisor para o almoxarifado separar."
      />

      <KpiStrip items={kpis} />

      <FilterBar>
        <SearchInput
          placeholder="Buscar por nº, cliente ou projeto…"
          value={buscaInput}
          onChange={(e) => setBuscaInput(e.target.value)}
          aria-label="Buscar requisições"
        />
        <Select
          value={filtroSituacao}
          onChange={(e) => setFiltroSituacao(e.target.value)}
          aria-label="Filtrar por status"
        >
          <option value="">Todos os status</option>
          {SITUACOES_SEPARACAO.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </Select>

        <div style={{ flex: 1 }} />

        <ViewToggle role="tablist" aria-label="Modo de visualização">
          {VISTAS.map((v) => {
            const Icone = v.icone;
            return (
              <ToggleBtn
                key={v.id}
                $on={vista === v.id}
                onClick={() => setVista(v.id)}
                role="tab"
                aria-selected={vista === v.id}
              >
                <Icone size={14} /> {v.label}
              </ToggleBtn>
            );
          })}
        </ViewToggle>

        <button className="btn btn--secondary btn--sm" onClick={carregar} aria-label="Atualizar">
          <FiRefreshCw size={14} />
        </button>
        {podeConfigurar ? (
          <button className="btn btn--secondary btn--sm" onClick={() => setColunasOpen(true)}>
            <FiSliders size={14} /> Colunas
          </button>
        ) : null}
      </FilterBar>

      {loading ? (
        <Carregando />
      ) : vazio ? (
        <EmptyBox>Nenhuma requisição de separação{filtroSituacao || busca ? " com esses filtros" : " ainda"}.</EmptyBox>
      ) : vista === "tabela" ? (
        <Tabela
          requisicoes={filtradas}
          selecionadas={selecionadas}
          onToggleSel={toggleSel}
          onAbrir={abrirDetalhe}
          podeGerir={podeGerir}
          vocab={VOCAB}
          campoItens="total_itens"
          comRevisao
        />
      ) : vista === "kanban" ? (
        <Kanban
          requisicoes={todas}
          colunas={colunas}
          onSoltar={soltarNaColuna}
          onAbrir={abrirDetalhe}
          podeGerir={podeGerir}
          vocab={VOCAB}
          campoItens="total_itens"
          comRevisao
        />
      ) : vista === "linha" ? (
        <LinhaTempo requisicoes={filtradas} onAbrir={abrirDetalhe} vocab={VOCAB} campoItens="total_itens" />
      ) : (
        <Calendario requisicoes={filtradas} onAbrir={abrirDetalhe} vocab={VOCAB} />
      )}

      {podeGerir && selecionadas.size > 0 ? (
        <BatchBar>
          <strong>{selecionadas.size} selecionada(s)</strong>
          <div style={{ flex: 1 }} />
          <button className="btn btn--secondary btn--sm" onClick={() => acaoLote("iniciar")}>Iniciar</button>
          <button className="btn btn--secondary btn--sm" onClick={() => acaoLote("separar")}>Marcar separadas</button>
          <button className="btn btn--secondary btn--sm" onClick={() => acaoLote("cancelar")}>Cancelar ferragens</button>
          <button className="btn btn--secondary btn--sm" onClick={() => setSelecionadas(new Set())}>Limpar</button>
        </BatchBar>
      ) : null}

      <Detalhe
        open={!!detalheId}
        req={detalheReq}
        loading={detalheLoading}
        onClose={fecharDetalhe}
        onAcao={acaoDoDetalhe}
        onImprimir={onImprimir}
        onSepararEImprimir={separarEImprimir}
        onReimprimirComprovante={reimprimirComprovante}
        podeGerir={podeGerir}
      />

      <ColunasPanel
        open={colunasOpen}
        colunas={colunas}
        situacoes={SITUACOES_SEPARACAO}
        onClose={() => setColunasOpen(false)}
        onSalvar={salvarColunas}
      />
    </PageContainer>
  );
}
